import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeError } from '../errors.js';
import { findNewAuditId, type AdminHttpClient } from '../http.js';
import type { AdminUserView, ServiceRole, ToastMessage } from '../types.js';
import { FormField } from '../components/FormField.js';
import { DataTable } from '../components/DataTable.js';
import { useFetchOnMount } from '../hooks/useFetchOnMount.js';
import { parseCsv } from '../utils/csv.js';
import { formatResetAt } from '../utils/reset-at.js';
import {
  planBulkAccess,
  summarizeBulkAccess,
  togglePageSelection,
  type BulkAccessOutcome,
  type SelectedUser
} from './users/bulk-access.js';

// The role a new user is created with. `member` and `admin` (Tenant Admin) are
// real tenant membership roles; `platform_admin` is NOT a membership role — it
// provisions a member and then grants platform-admin via the platform registry
// (see submit()). The platform-admin option is only offered to platform-admin
// operators, since the grant needs the platform-scoped token.
type CreateRole = 'member' | 'admin';
const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

// Derived from the client method so it stays in sync if the API response shape changes.
type IssuedInvite = Awaited<ReturnType<AdminHttpClient['inviteUser']>>;
type UserList = Awaited<ReturnType<AdminHttpClient['listUsers']>>;
type UserListRequest = { page: number; query: string; nonce: number };
type BulkUser = { email: string; name: string; password: string; role: CreateRole; error?: string };
type BulkFailure = { user: BulkUser; message: string; retryable: boolean };
type BulkAccessFailure = { id: string; message: string };

function parseBulkUsers(text: string): BulkUser[] {
  const rows = parseCsv(text);
  if (rows[0]?.[0]?.toLowerCase() === 'email') rows.shift();
  return rows.map(([email = '', name = '', password = '', inputRole = 'member']) => {
    const role = inputRole.toLowerCase() as CreateRole;
    return {
      email,
      name,
      password,
      role,
      ...(email && name && password.length >= 8 && (role === 'member' || role === 'admin') ? {} : { error: 'invalid' })
    };
  });
}

function isTenantAdmin(user: AdminUserView): boolean {
  return user.roles.includes('admin') || user.roles.includes('owner');
}

function tenantAdminRoles(user: AdminUserView): ServiceRole[] {
  return [...new Set<ServiceRole>([...user.roles, 'admin'])];
}

export function UsersScreen({
  http,
  isPlatformAdmin,
  isPartnerAdmin,
  onToast
}: {
  /** Tenant-scoped client (act-as token for platform admins) — user CRUD,
   *  seats, roles, enable/disable all run through this. */
  http: AdminHttpClient;
  /** Platform-scoped client — ONLY for platform-admin grant/revoke, which the
   *  act-as tenant token is not authorized for. */
  isPlatformAdmin: boolean;
  isPartnerAdmin: boolean;
  onToast: (toast: ToastMessage) => void;
}) {
  const { t, i18n } = useTranslation();
  // Server-driven list: only the current page is fetched, search runs on the
  // service (email/name substring) — a tenant with thousands of users never
  // pulls the full list into the browser.
  const [users, setUsers] = useState<AdminUserView[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState('');
  const [listRequest, setListRequest] = useState<UserListRequest>({
    page: 0,
    query: '',
    nonce: 0
  });
  const searchTimer = useRef<number | undefined>(undefined);
  // Password is the default: desktop and web-console sign-in accept passwords.
  // Invite-token mode is retained only for API integrations that implement the
  // login/start + login/complete handshake themselves.
  const [mode, setMode] = useState<'invite' | 'password' | 'bulk'>('password');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<CreateRole>('member');
  const [issuedInvite, setIssuedInvite] = useState<IssuedInvite | null>(null);
  const [bulkText, setBulkText] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState<{ applied: number; failed: number }>();
  const [bulkFailures, setBulkFailures] = useState<BulkFailure[]>([]);
  const [grantDesktopAfterCreate, setGrantDesktopAfterCreate] = useState(false);
  const [selected, setSelected] = useState<Map<string, SelectedUser>>(new Map());
  const [bulkAccessBusy, setBulkAccessBusy] = useState(false);
  const [bulkAccessFailures, setBulkAccessFailures] = useState<BulkAccessFailure[]>([]);
  const bulkUsers = useMemo(() => parseBulkUsers(bulkText), [bulkText]);

  const load = useCallback(
    (nextPage: number, nextQuery: string, force = false) => {
      // Any direct load (pager click, post-action reload) supersedes a
      // still-pending debounced search.
      window.clearTimeout(searchTimer.current);
      setListRequest((current) => {
        if (!force && current.page === nextPage && current.query === nextQuery) return current;
        return { page: nextPage, query: nextQuery, nonce: current.nonce + 1 };
      });
    },
    []
  );

  const fetchUsers = useCallback(
    async (_signal: AbortSignal) =>
      http.listUsers({
        limit: PAGE_SIZE,
        offset: listRequest.page * PAGE_SIZE,
        ...(listRequest.query.trim() ? { q: listRequest.query.trim() } : {})
      }),
    [http, listRequest]
  );

  const onUsersLoaded = useCallback(
    (result: UserList) => {
      // E.g. the last user on the last page was just disabled/filtered out —
      // step back instead of showing an empty page.
      if (result.items.length === 0 && listRequest.page > 0) {
        const lastPage = Math.max(0, Math.ceil(result.total / PAGE_SIZE) - 1);
        load(Math.min(listRequest.page - 1, lastPage), listRequest.query);
        return;
      }
      setUsers(result.items);
      setTotal(result.total);
      setPage(listRequest.page);
    },
    [listRequest, load]
  );

  const onUsersError = useCallback(
    (error: unknown) => {
      onToast({ tone: 'error', message: describeError(error, t) });
    },
    [onToast, t]
  );

  useFetchOnMount({ fetcher: fetchUsers, onData: onUsersLoaded, onError: onUsersError });

  // Actions (invite/disable/...) reload in place: same page, same search.
  async function reload() {
    load(page, query, true);
  }

  useEffect(() => {
    return () => window.clearTimeout(searchTimer.current);
  }, []);

  useEffect(() => {
    setSelected(new Map());
    setBulkAccessFailures([]);
  }, [http]);

  function onSearchChange(value: string) {
    setQuery(value);
    setSelected(new Map());
    setBulkAccessFailures([]);
    window.clearTimeout(searchTimer.current);
    searchTimer.current = window.setTimeout(() => {
      load(0, value);
    }, SEARCH_DEBOUNCE_MS);
  }

  async function writeAndToast<T>(
    action: () => Promise<T>,
    expected: { eventType?: string; targetId?: string }
  ): Promise<T | undefined> {
    try {
      const before = await http.listAudit();
      const result = await action();
      await reload();
      const auditId = findNewAuditId(before, await http.listAudit(), expected);
      onToast({
        tone: 'success',
        message: t('common.changedWithAudit', {
          auditId: auditId ?? t('common.pending')
        })
      });
      return result;
    } catch (error) {
      onToast({ tone: 'error', message: describeError(error, t) });
      return undefined;
    }
  }

  const PASSWORD_MIN = 8;

  // Platform Admin is granted via a separate platform-scoped call after the
  // user is provisioned, so the membership role is always a real tenant role.
  const membershipRole: ServiceRole = role;

  // Grant platform-admin to a freshly-created user. Best-effort follow-up: the
  // user already exists, so a grant failure is surfaced but does not roll back
  // creation — the operator can retry the grant from the row action.
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (mode === 'bulk') {
      setBulkBusy(true);
      let applied = 0;
      let failed = 0;
      const failures: BulkFailure[] = [];
      for (const user of bulkUsers) {
        if (user.error) { failed += 1; failures.push({ user, message: t('users.bulkInvalid'), retryable: true }); continue; }
        try {
          const created = await http.createUserWithPassword(user.email, user.name, user.password, [user.role]);
          if (grantDesktopAfterCreate) {
            try {
              await http.updateDownloadAccess(created.user.id, { desktopAccess: true });
            } catch {
              failed += 1;
              failures.push({ user, message: t('users.bulkCreatedButGrantFailed'), retryable: false });
              continue;
            }
          }
          applied += 1;
        } catch (error) {
          failed += 1;
          failures.push({ user, message: describeError(error, t), retryable: true });
        }
      }
      setBulkResult({ applied, failed });
      setBulkFailures(failures);
      setBulkBusy(false);
      setSelected(new Map());
      await reload();
      return;
    }
    if (mode === 'password') {
      const created = await writeAndToast(
        () => http.createUserWithPassword(email.trim(), name.trim(), password, [membershipRole]),
        {
          eventType: 'admin.user.created'
        }
      );
      if (!created) return;
      // No invite token to surface — the user is active and can sign in to the
      // desktop with this password immediately.
      setIssuedInvite(null);
      setEmail('');
      setName('');
      setPassword('');
      return;
    }
    const issued = await writeAndToast(() => http.inviteUser(email.trim(), name.trim(), [membershipRole]), {
      eventType: 'admin.user.invited'
    });
    if (!issued) return;
    // The invite token is single-use and only returned by this response — surface it
    // now so the operator can hand it to the invitee. It is never retrievable again.
    setIssuedInvite(issued);
    setEmail('');
    setName('');
  }

  async function copyInvite(token: string) {
    try {
      await navigator.clipboard.writeText(token);
      onToast({ tone: 'info', message: t('users.tokenCopied') });
    } catch {
      onToast({ tone: 'error', message: t('users.copyFailed') });
    }
  }

  async function applyBulkAccess(target: boolean) {
    const plan = planBulkAccess(selected, target);
    if (!window.confirm(t('users.bulkAccessConfirm', {
      count: selected.size,
      action: target ? t('users.bulkAllowDesktop') : t('users.bulkBlockDesktop')
    }))) return;
    setBulkAccessBusy(true);
    const outcomes: BulkAccessOutcome[] = plan.skip.map(() => 'skipped');
    const failures: BulkAccessFailure[] = [];
    for (const id of plan.apply) {
      try {
        await http.updateDownloadAccess(id, { desktopAccess: target });
        outcomes.push('applied');
      } catch (error) {
        outcomes.push('failed');
        failures.push({ id, message: describeError(error, t) });
      }
    }
    const summary = summarizeBulkAccess(outcomes);
    setBulkAccessFailures(failures);
    setSelected(new Map());
    setBulkAccessBusy(false);
    await reload();
    onToast({ tone: summary.failed ? 'error' : 'success', message: t('users.bulkAccessResult', summary) });
  }

  const allPageSelected = users.length > 0 && users.every((user) => selected.has(user.id));

  return (
    <section className="content-stack">
      <p className="screen-help">{t('screenHelp.users')}</p>
      {issuedInvite && (
        <div className="panel invite-callout">
          <strong>{t('users.inviteIssued')}</strong>
          <p className="muted">{t('users.inviteNoMailNotice')}</p>
          <dl className="invite-fields">
            <div>
              <dt>{t('users.userId')}</dt>
              <dd className="mono">{issuedInvite.user.id}</dd>
            </div>
            <div>
              <dt>{t('users.inviteToken')}</dt>
              <dd className="mono">{issuedInvite.inviteToken}</dd>
            </div>
            <div>
              <dt>{t('users.expires')}</dt>
              <dd>{t('users.inviteExpiresAt', {
                time: formatResetAt(issuedInvite.expiresAt, i18n.resolvedLanguage ?? i18n.language)
              })}</dd>
            </div>
          </dl>
          <div className="row-actions">
            <button type="button" className="secondary" onClick={() => void copyInvite(issuedInvite.inviteToken)}>
              {t('users.copyToken')}
            </button>
            <button type="button" className="secondary" onClick={() => setIssuedInvite(null)}>
              {t('common.dismiss')}
            </button>
          </div>
        </div>
      )}
      {/* autoComplete is off across the board: this form creates OTHER users, so
          the browser must not autofill the signed-in operator's saved email /
          password into it. Chrome ignores `off` on password fields, so the
          password input uses `new-password`, the reliable suppressant. */}
      <form className="toolbar-form user-create-form" onSubmit={submit} autoComplete="off">
        <h2>{t('users.createUser')}</h2>
        <label className="form-wide"><span id="user-create-mode">{t('users.creationMode')}</span>
        <select aria-labelledby="user-create-mode" value={mode} onChange={(event) => setMode(event.target.value as 'invite' | 'password' | 'bulk')}>
          <option value="password">{t('users.createWithPassword')}</option>
          <option value="invite">{t('users.inviteViaEmail')}</option>
          <option value="bulk">{t('users.bulkCreate')}</option>
        </select></label>
        {mode === 'invite' && <span className="muted">{t('users.inviteModeHint')}</span>}
        {mode === 'bulk' ? <>
          <label className="form-wide">{t('users.csvFile')}<input type="file" accept=".csv,text/csv" onChange={(event) => { const file = event.target.files?.[0]; if (file) void file.text().then(setBulkText); }} /></label>
          <label className="form-wide">{t('users.csvText')}<textarea value={bulkText} onChange={(event) => setBulkText(event.target.value)} placeholder={t('users.bulkPlaceholder')} rows={5} /></label>
          <span className="muted">{t('users.bulkPreview', { total: bulkUsers.length, valid: bulkUsers.filter((user) => !user.error).length })}</span>
          <label><input type="checkbox" checked={grantDesktopAfterCreate} onChange={(event) => setGrantDesktopAfterCreate(event.target.checked)} /> {t('users.grantDesktopAfterCreate')}</label>
          {bulkResult && <span className="muted">{t('users.bulkResult', bulkResult)}</span>}
          <button disabled={bulkBusy || bulkAccessBusy || bulkUsers.length === 0 || bulkUsers.some((user) => user.error)}>{t('users.bulkApply')}</button>
          {bulkFailures.some(({ retryable }) => retryable) && <button type="button" disabled={bulkBusy} onClick={() => { setBulkText(bulkFailures.filter(({ retryable }) => retryable).map(({ user }) => [user.email, user.name, user.password, user.role].join(',')).join('\n')); setBulkFailures([]); }}>{t('users.bulkRetry')}</button>}
          {bulkUsers.length > 0 && <table><thead><tr><th>{t('users.email')}</th><th>{t('users.name')}</th><th>{t('users.status')}</th></tr></thead><tbody>{bulkUsers.map((user, index) => <tr key={`${user.email}-${index}`}><td>{user.email}</td><td>{user.name}</td><td>{user.error ? t('users.bulkInvalid') : bulkFailures.find((failure) => failure.user.email === user.email)?.message ?? t('users.bulkReady')}</td></tr>)}</tbody></table>}
        </> : <>
        <FormField label={t('users.email')}
          placeholder={t('users.email')}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="off"
        />
        <FormField label={t('users.name')}
          placeholder={t('users.name')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          autoComplete="off"
        />
        {mode === 'password' && (
          <FormField label={t('users.passwordMin', { min: PASSWORD_MIN })}
            type="password"
            placeholder={t('users.passwordMin', { min: PASSWORD_MIN })}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
          />
        )}
        <label><span id="user-create-role">{t('users.role')}</span><select aria-labelledby="user-create-role" value={role} onChange={(event) => setRole(event.target.value as CreateRole)}>
          <option value="member">{t('users.roleMember')}</option>
          {(isPlatformAdmin || isPartnerAdmin) && <option value="admin">{t('users.roleTenantAdmin')}</option>}
        </select></label>
        <button className="form-wide" disabled={bulkAccessBusy || !email.trim() || !name.trim() || (mode === 'password' && password.length < PASSWORD_MIN)}>
          {mode === 'password' ? t('users.createUser') : t('users.inviteUser')}
        </button>
        </>}
      </form>
      {selected.size > 0 && (
        <div className="panel toolbar-form">
          <strong>{t('users.selectedCount', { count: selected.size })}</strong>
          <button type="button" disabled={bulkAccessBusy || bulkBusy} onClick={() => void applyBulkAccess(true)}>{t('users.bulkAllowDesktop')}</button>
          <button type="button" disabled={bulkAccessBusy || bulkBusy} onClick={() => void applyBulkAccess(false)}>{t('users.bulkBlockDesktop')}</button>
          <button type="button" className="secondary" disabled={bulkAccessBusy || bulkBusy} onClick={() => setSelected(new Map())}>{t('users.clearSelection')}</button>
        </div>
      )}
      {bulkAccessFailures.length > 0 && (
        <table><thead><tr><th>{t('users.userId')}</th><th>{t('users.status')}</th></tr></thead><tbody>{bulkAccessFailures.map((failure) => <tr key={failure.id}><td className="mono">{failure.id}</td><td>{failure.message}</td></tr>)}</tbody></table>
      )}
      <DataTable
        rows={users}
        empty={t('users.noUsers')}
        mobileCards
        searchPlaceholder={t('users.searchPlaceholder')}
        pageSize={PAGE_SIZE}
        getRowKey={(user) => user.id}
        server={{
          total,
          page,
          onPageChange: (nextPage) => void load(nextPage, query),
          onSearchChange
        }}
        columns={[
          {
            key: 'selection',
            header: <input
              type="checkbox"
              aria-label={t('users.selectAllOnPage')}
              checked={allPageSelected}
              disabled={users.length === 0 || bulkAccessBusy}
              onChange={(event) => setSelected((current) => togglePageSelection(
                current,
                users.map((user) => ({ id: user.id, desktopAccess: user.desktopAccess })),
                event.target.checked
              ))}
            />,
            render: (user) => <input
              type="checkbox"
              aria-label={user.email}
              checked={selected.has(user.id)}
              disabled={bulkAccessBusy}
              onChange={(event) => setSelected((current) => togglePageSelection(
                current,
                [{ id: user.id, desktopAccess: user.desktopAccess }],
                event.target.checked
              ))}
            />
          },
          { key: 'email', header: t('users.email'), render: (user) => user.email },
          { key: 'name', header: t('users.name'), render: (user) => user.name },
          {
            key: 'status',
            header: t('users.status'),
            render: (user) => (
              <span className="badge-group">
                <span className={`badge ${user.status}`}>{user.status}</span>
                {isTenantAdmin(user) && <span className="badge active">{t('users.roleTenantAdmin')}</span>}
                {user.isPlatformAdmin && <span className="badge active">{t('users.rolePlatformAdmin')}</span>}
                {user.isPartnerAdmin && <span className="badge active">{t('users.rolePartnerAdmin')}</span>}
              </span>
            )
          },
          {
            key: 'seatStatus',
            header: t('users.seat'),
            render: (user) => <span className={`badge ${user.seatStatus}`}>{user.seatStatus}</span>
          },
          {
            key: 'desktopAccess',
            header: t('users.desktopAccess'),
            render: (user) => (
              <span className={`badge ${user.desktopAccess ? 'active' : 'disabled'}`}>
                {user.desktopAccess ? t('users.desktopEnabled') : t('users.desktopDisabled')}
              </span>
            )
          },
          {
            key: 'actions',
            header: t('common.actions'),
            render: (user) => (
              <details className="user-row-actions"
                onKeyDown={(event) => {
                  if (event.key !== 'Escape') return;
                  event.preventDefault();
                  event.currentTarget.open = false;
                  event.currentTarget.querySelector('summary')?.focus();
                }}
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) event.currentTarget.open = false;
                }}
                onClick={(event) => {
                  if (!(event.target instanceof Element) || !event.target.closest('button')) return;
                  event.currentTarget.open = false;
                  event.currentTarget.querySelector('summary')?.focus();
                }}>
                <summary aria-label={t('users.rowActionsFor', { email: user.email })}>{t('common.actions')}</summary>
                <div className="row-actions">
                {(isPlatformAdmin || isPartnerAdmin) && <button
                  className="secondary"
                  disabled={isTenantAdmin(user)}
                  onClick={() =>
                    void writeAndToast(() => http.updateUserRoles(user.id, tenantAdminRoles(user)), {
                      eventType: 'admin.user.updated',
                      targetId: user.id
                    })
                  }
                >
                  {t('users.makeTenantAdmin')}
                </button>}
                <button
                  className="secondary"
                  disabled={bulkAccessBusy || bulkBusy}
                  onClick={() =>
                    void writeAndToast(
                      () => http.updateDownloadAccess(user.id, { desktopAccess: !user.desktopAccess }),
                      { eventType: 'admin.download_channel.updated', targetId: user.id }
                    )
                  }
                >
                  {user.desktopAccess ? t('users.blockDesktop') : t('users.allowDesktop')}
                </button>
                {user.status === 'active' ? (
                  <button
                    className="secondary"
                    onClick={() => void writeAndToast(() => http.disableUser(user.id), { eventType: 'admin.user.disabled', targetId: user.id })}
                  >
                    {t('users.disable')}
                  </button>
                ) : (
                  <button
                    className="secondary"
                    onClick={() => void writeAndToast(() => http.enableUser(user.id), { eventType: 'admin.user.enabled', targetId: user.id })}
                  >
                    {t('users.enable')}
                  </button>
                )}
                <button
                  className="danger"
                  onClick={() => {
                    if (!window.confirm(t('users.confirmDelete', { email: user.email }))) return;
                    void writeAndToast(() => http.deleteUser(user.id), {
                      eventType: 'admin.user.deleted',
                      targetId: user.id
                    });
                  }}
                >
                  {t('users.delete')}
                </button>
                </div>
              </details>
            )
          }
        ]}
      />
    </section>
  );
}
